<template>
  <hb-basic-page
    v-if="$isTenantAdmin || $isOfficeUser"
    :action-buttons="actionButtons"
    :fill-height="true"
    :hide-app-bar="$vuetify.breakpoint.smAndDown"
    :subtle-loading="!loading && updating"
    :title="$tc('production_timeline.title', 2)"
    class="production-lines-view"
    fluid
    no-container-padding
    no-gutters
  >
    <v-fade-transition>
      <v-col class="fill-height production-timeline-content" cols="12">
        <v-row :class="[$vuetify.breakpoint.smAndDown && 'flex-column flex-nowrap', unqueuedMinimized && 'minimize-unqueued']"
               class="fill-height"
               no-gutters>
          <!-- Unscheduled (top) -->
          <v-col :class="$vuetify.breakpoint.mdAndUp && 'px-3 pt-3'" class="unqueued" cols="12" lg="3" md="4">
            <unscheduled-column
              :has-any-projects="projectLogs.length > 0"
              :is-admin="$isTenantAdmin"
              :loading="loading"
              :production-lines="productionLines"
              :project-logs="unscheduledProjectLogs"
              @assign-to-line="handleAssignToLine"
              @drag-change="handleDragChangeUnscheduled"
              @reload="reload"/>
          </v-col>

          <!-- Toggle bar for unscheduled section (mobile) -->
          <div v-if="$vuetify.breakpoint.smAndDown" class="unqueued-toggle-bar d-flex align-center justify-center"
               @click="unqueuedMinimized = !unqueuedMinimized">
            <v-btn icon x-small>
              <v-icon small>{{ unqueuedMinimized ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-btn>
            <v-badge v-if="unqueuedMinimized && unscheduledProjectLogs.length > 0"
                     :content="unscheduledProjectLogs.length"
                     color="primary"
                     inline/>
          </div>

          <!-- Production Lines (bottom, scrollable) -->
          <v-col :class="$vuetify.breakpoint.mdAndUp && 'px-3 pb-3'" class="production-lines-container fill-height overflow-x-auto" cols="12" lg="9" md="8">
            <!-- Production line tabs (mobile only) -->
            <v-tabs v-if="$vuetify.breakpoint.smAndDown && productionLines.length > 1"
                    v-model="activeLineTab"
                    dark dense grow
                    height="36"
                    class="production-line-tabs flex-grow-0">
              <v-tab v-for="(line, index) in productionLines"
                     :key="line.id"
                     @click="scrollToLine(index)">
                {{ line.name }}
              </v-tab>
            </v-tabs>

            <v-row class="flex-nowrap" ref="productionLinesRow" no-gutters
                   :class="$vuetify.breakpoint.smAndDown ? 'production-lines-scroll' : 'fill-height'"
                   @scroll="onProductionLinesScroll">
              <v-col v-for="line in productionLines"
                     :key="line.id"
                     :style="productionLines.length > 1 ? ($vuetify.breakpoint.smAndDown ? 'min-width: 100%; max-width: 100%;' : 'min-width: 350px; max-width: 50%;') : ''"
                     class="fill-height production-line-col">
                <production-line-column
                  :is-admin="$isTenantAdmin"
                  :loading="loading"
                  :production-line="line"
                  :production-lines="productionLines"
                  :updating="updating"
                  :weeks="scheduledByLine[line.id] || []"
                  @drag-change="handleScheduledDragChange"
                  @assign-to-line="handleAssignToLine"
                  @unschedule="handleUnschedule"
                  @refresh="() => refreshScheduledTimes(line.id)"/>
              </v-col>
            </v-row>

            <!-- Scroll indicator dots (mobile only) -->
            <div v-if="$vuetify.breakpoint.smAndDown && productionLines.length > 1" class="scroll-indicator-dots">
              <div v-for="(line, index) in productionLines"
                   :key="line.id"
                   :class="['scroll-dot', { active: activeLineTab === index }]"/>
              <span class="scroll-dot-label ml-2">{{ activeLineTab + 1 }}/{{ productionLines.length }}</span>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-fade-transition>
  </hb-basic-page>
</template>

<script>
import productionLineApi from '@/api/productionline';
import projectApi from '@/api/project';
import dayjs from "dayjs";
import UnscheduledColumn from './components/UnscheduledColumn';
import ProductionLineColumn from './components/ProductionLineColumn';
import orderBy from "lodash/orderBy";

export default {
  name: 'ProductionTimelineView',
  components: { UnscheduledColumn, ProductionLineColumn },
  data() {
    return {
      loading: true,
      options: {},
      pagedResult: {},
      searchString: '',
      productionLines: [],
      projectLogs: [],
      saving: false,
      dayHeight: 240,
      unqueuedMinimized: false,
      activeLineTab: 0,
      unscheduledProjectLogs: [],
      updating: false,
      currentWeek: this.$formatWeekNumber(dayjs()),
    };
  },
  async mounted() {
    if (!this.$hasMatchingTenantRoles(['tenant_admin', 'tenant_office'])) {
      this.$router.replace({name: 'access_denied'});
      return;
    }
    await this.reload();
    this.$nextTick(() => {
      const row = this.$refs.productionLinesRow?.$el || this.$refs.productionLinesRow;
      if (row) {
        row.addEventListener('scroll', this.onProductionLinesScroll);
      }
    });
  },
  beforeDestroy() {
    const row = this.$refs.productionLinesRow?.$el || this.$refs.productionLinesRow;
    if (row) {
      row.removeEventListener('scroll', this.onProductionLinesScroll);
    }
  },
  computed: {
    productionTimeExceptions() {
      const o = {};
      this.productionLines.forEach(l => {
        l.productionTimeExceptions?.forEach(plte => {
          if (o[`${l.id}`]) {
            o[`${l.id}`].push(plte);
            return;
          }
          o[`${l.id}`] = [plte];
        });
      })
      return o;
    },
    scheduledByLine() {
      const result = {};
      this.productionLines.forEach(line => {
        const lineProjects = orderBy(
          this.projectLogs.filter(p =>
            p.productionLineId === line.id &&
            p.productionStartDate &&
            p.productionTime != null &&
            ["CANT_START", "CAN_START", "PAUSED", "MANUFACTURING"].includes(p.status)
          ),
          ['productionStartDate'],
          ['asc']
        );
        result[line.id] = this.parseWeeksForLine(lineProjects, line);
      });
      return result;
    },
    actionButtons() {
      return {};
    },
  },
  methods: {
    async handleScheduledDragChange({ event, weekNumber, productionLineId }) {
      const e = event;
      const weeks = this.scheduledByLine[productionLineId] || [];
      let startDate = null;
      let projectLogId = null;
      let timelineStartDate = dayjs().startOf("hour").toISOString();
      const weekIndex = weeks.findIndex(w => w.weekNumber === weekNumber);

      if (e.added) {
        if (e.added.newIndex > 0) {
          startDate = weeks[weekIndex]?.projects[e.added.newIndex - 1]?.productionEndDate;
        } else {
          if (weekIndex > 0) {
            let earlierProjectFound = false;
            let step = 1;
            while (!earlierProjectFound) {
              if (weeks[weekIndex - step]?.projects[weeks[weekIndex - step]?.projects.length - 1]?.productionEndDate) {
                startDate = weeks[weekIndex - step]?.projects[weeks[weekIndex - step]?.projects.length - 1]?.productionEndDate
                earlierProjectFound = true;
              } else {
                if (weeks[weekIndex - (step + 1)]) {
                  step++;
                } else {
                  startDate = dayjs().startOf("hour").toISOString();
                  earlierProjectFound = true;
                }
              }
            }
          } else {
            startDate = dayjs().startOf("hour").toISOString();
          }
        }
        projectLogId = e.added.element.id
      } else if (e.moved) {
        if (e.moved.newIndex > 0) {
          startDate = weeks[weekIndex]?.projects[e.moved.newIndex - 1]?.productionEndDate || dayjs().startOf("hour").toISOString();
        } else {
          startDate = weeks[weekIndex - 1]?.projects[weeks[weekIndex - 1]?.projects.length - 1]?.productionEndDate || dayjs().startOf("hour").toISOString();
        }
        projectLogId = e.moved.element.id
      }

      if (projectLogId && startDate) {
        this.updating = true;
        let project = this.projectLogs.find(p => p.id === projectLogId)
        project.productionStartDate = startDate;
        project.productionEndDate = dayjs(startDate).add(project.productionTime, 'hour').toISOString();

        try {
          await projectApi.reschedule(productionLineId, projectLogId, startDate, timelineStartDate);
          await this.reloadProjects()
        } catch (e) {
          this.$showErrorNotification(this.$t('error.unexpected_error'));
        }
        this.updating = false;
      }
    },
    async handleDragChangeUnscheduled(e) {
      if (e.added && e.added.element) {
        this.updating = true;
        let element = e.added.element;
        try {
          await projectApi.reschedule(element.productionLineId, element.id, null, null);
          await this.reloadProjects()
        } catch (e) {
          this.$showErrorNotification(this.$t('error.unexpected_error'));
        }
        this.updating = false;
      }
    },
    async reloadProjects() {
      try {
        this.projectLogs = (await projectApi.getLogsPaged({
          searchString: null,
          done: false,
          noScheduling: false,
        }, {page: 1, itemsPerPage: 1000}))?.content;
        this.unscheduledProjectLogs = this.projectLogs.filter(p =>
          !p.productionStartDate &&
          p.productionTime != null &&
          ["CANT_START", "CAN_START", "PAUSED", "MANUFACTURING"].includes(p.status)
        );
      } catch (e) {
        this.$handleApiError(e);
      }
    },
    parseWeeksForLine(scheduledProjectLogs, line) {
      const weeks = [];
      const lastProjectStartDate = dayjs(scheduledProjectLogs[scheduledProjectLogs.length - 1]?.productionStartDate);
      let productionTimeExceptions = this.productionTimeExceptions[line.id];
      if (productionTimeExceptions) {
        productionTimeExceptions = productionTimeExceptions.sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      const lastExceptionDate = productionTimeExceptions ? dayjs(productionTimeExceptions[productionTimeExceptions.length - 1].date) : null;

      if (lastProjectStartDate.isValid() || lastExceptionDate) {
        let weekDate = scheduledProjectLogs[0]?.productionStartDate
          ? dayjs(scheduledProjectLogs[0].productionStartDate).startOf('week')
          : dayjs().startOf('week');
        while ((lastProjectStartDate.isValid() && weekDate.isBefore(lastProjectStartDate)) || (lastExceptionDate && weekDate.isBefore(lastExceptionDate))) {
          weeks.push({weekNumber: this.$formatWeekNumber(weekDate), projects: [], productionExceptions: []});
          weekDate = weekDate.add(1, 'week');
        }
      } else {
        weeks.push({weekNumber: this.$formatWeekNumber(dayjs()), projects: [], productionExceptions: []});
      }

      scheduledProjectLogs.forEach(project => {
        const weekNumber = this.$formatWeekNumber(project.productionStartDate);
        if (weekNumber) {
          const weekNumberIndex = weeks.findIndex(w => w.weekNumber === weekNumber);
          if (weekNumberIndex > -1) {
            if (!project || !project.project) return;
            weeks[weekNumberIndex].projects.push(project);
          } else {
            if (!project || !project.project) return;
            weeks.push({weekNumber: weekNumber, projects: [project], productionExceptions: []});
          }
        }
      });

      if (productionTimeExceptions) {
        productionTimeExceptions.forEach(exception => {
          const exceptionDate = dayjs(exception.date);
          if (exceptionDate.isBefore(dayjs().startOf('week'))) return;
          const weekNumber = this.$formatWeekNumber(exception.date);
          if (weekNumber) {
            const weekNumberIndex = weeks.findIndex(w => w.weekNumber === weekNumber);
            if (weekNumberIndex > -1) {
              weeks[weekNumberIndex].productionExceptions.push(exception);
            } else {
              weeks.push({weekNumber: weekNumber, projects: [], productionExceptions: [exception]});
            }
          }
        });
      }

      return weeks;
    },
    async reload() {
      try {
        this.productionLines = await productionLineApi.getProductionLines();
        await this.reloadProjects();
      } catch (e) {
        this.$showErrorNotification(this.$t('projects.errors.error_loading_projects'));
      }
      this.loading = false;
    },
    async handleAssignToLine({ projectLog, productionLine }) {
      this.updating = true;
      try {
        const startDate = dayjs().startOf('hour').toISOString();
        const timelineStartDate = dayjs().startOf('hour').toISOString();
        await projectApi.reschedule(productionLine.id, projectLog.id, startDate, timelineStartDate);
        await this.reloadProjects();
      } catch (e) {
        this.$showErrorNotification(this.$t('error.unexpected_error'));
      }
      this.updating = false;
    },
    scrollToLine(index) {
      const row = this.$refs.productionLinesRow?.$el || this.$refs.productionLinesRow;
      if (!row) return;
      const cols = row.querySelectorAll('.production-line-col');
      if (cols[index]) {
        cols[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    },
    onProductionLinesScroll() {
      const row = this.$refs.productionLinesRow?.$el || this.$refs.productionLinesRow;
      if (!row) return;
      const cols = row.querySelectorAll('.production-line-col');
      if (cols.length === 0) return;
      const scrollLeft = row.scrollLeft;
      const colWidth = cols[0].offsetWidth;
      if (colWidth > 0) {
        this.activeLineTab = Math.round(scrollLeft / colWidth);
      }
    },
    async refreshScheduledTimes(lineId) {
      this.updating = true;
      try {
        await projectApi.refreshScheduledTimes(lineId);
        await this.reloadProjects();
      } catch (e) {
        this.$showErrorNotification(this.$t('error.unexpected_error'));
      }
      this.updating = false;
    },
    async handleUnschedule({ projectLog }) {
      this.updating = true;
      try {
        await projectApi.reschedule(projectLog.productionLineId, projectLog.id, null, null);
        await this.reloadProjects();
      } catch (e) {
        this.$showErrorNotification(this.$t('error.unexpected_error'));
      }
      this.updating = false;
    }
  },
};
</script>

<style lang="scss">
@media screen and (max-width: 959px) {
  .production-timeline-content {
    height: calc(100vh - 56px);
  }

  .unqueued {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 auto;
    max-height: 30%;
    transition: all 0.15s ease-in-out;
    overflow: auto;
  }

  .unqueued-toggle-bar {
    width: 100%;
    height: 32px;
    flex: 0 0 32px;
    background-color: #e0e0e0;
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    cursor: pointer;
  }

  .production-lines-container {
    width: 100% !important;
    min-width: 100% !important;
    flex: 1 1 auto;
    transition: all 0.15s ease-in-out;
    display: flex;
    flex-direction: column;
  }

  .scheduled-projects-card {
    z-index: 3;
    box-shadow: 0 -5px 12px -5px rgba(0, 0, 0, 0.3) !important;
  }

  .minimize-unqueued {
    .unqueued {
      flex: 0 0 0px;
      overflow: hidden;
    }

    .production-lines-container {
      flex: 1 1 auto;
    }
  }

  .production-line-tabs {
    flex: 0 0 36px;

    .v-tab {
      text-transform: none;
      font-size: 12px;
      letter-spacing: 0;
      min-width: 0;
    }
  }

  .production-lines-scroll {
    flex: 1 1 auto;
    overflow-x: auto;
    overflow-y: auto;
    scroll-snap-type: x mandatory;

    .production-line-col {
      scroll-snap-align: start;
    }
  }

  .scroll-indicator-dots {
    height: 24px;
    flex: 0 0 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    .scroll-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #bdbdbd;
      margin: 0 4px;
      transition: background-color 0.2s ease;

      &.active {
        background-color: var(--v-primary-base, #1976d2);
      }
    }

    .scroll-dot-label {
      font-size: 11px;
      color: #757575;
    }
  }
}

@media screen and (min-width: 960px) {
  .production-timeline-content {
    height: calc(100vh - 64px);
  }

  .unqueued {
    height: 100%;
    overflow-y: auto;
  }

  .production-lines-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .production-lines-container > .row {
    flex: 1;
    min-height: 0;
  }

  .production-line-col {
    display: flex;
    flex-direction: column;
  }

  .production-line-col .scheduled-projects-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .production-line-col .scheduled-projects-card > .v-card__text {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .queue-scroll {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
  }
}

.list-group {
  position: relative;
  z-index: 3;
}

$card-margin: 0px;

.project-card {
  margin: $card-margin 5px;
  position: relative;
  z-index: 2;
  overflow: hidden;
  border-bottom: 1px solid black;
}

.calendar-week-wrapper {
  border-bottom: 1px solid #eee;

  &:nth-child(odd) {
    background-color: #fdfdfd;
  }

  .calendar-week {
    .calendar-week-number {
      font-size: 11px;
      font-weight: 500;
    }

    &.even {
      background-color: #454545;
      color: white;
    }

    &.odd {
      background-color: #ededed;
      color: black;
    }
  }
}

</style>
